import { create } from 'zustand'
import { persist, createJSONStorage, devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { faqService, type Faq, type FaqTopic, type FaqSearchResult } from '@/services'

// Extended FAQ types for store
export interface VotedFaq {
  [faqId: number]: 'up' | 'down'
}

export interface FaqStore {
  // State
  faqsLoading: boolean
  relatedFaqsLoading: boolean
  allFaqTopics: FaqTopic[]
  selectedFaq: Faq | null
  relatedFaqs: Faq[]
  faqSearchResults: Faq[]
  votedFaqs: VotedFaq
  previouslySelectedFaqTopic: FaqTopic | null
  selectedFaqTopic: FaqTopic | null

  // Computed selectors
  getFaqById: (id: number) => Faq | undefined
  getFaqBySlug: (slug: string) => Faq | undefined
  getFaqTopicById: (id: number) => FaqTopic | undefined
  getFaqTopicBySlug: (slug: string) => FaqTopic | undefined
  hasSearchResults: () => boolean
  getVoteForFaq: (faqId: number) => 'up' | 'down' | null

  // Actions
  setFaqsLoading: (loading: boolean) => void
  setFaqTopics: (topics: FaqTopic[]) => void
  setSelectedFaq: (faq: Faq | null) => void
  setRelatedFaqs: (faqs: Faq[]) => void
  setFaqSearchResults: (results: Faq[]) => void
  setSelectedFaqTopic: (topic: FaqTopic | null) => void
  setPreviouslySelectedFaqTopic: (topic: FaqTopic | null) => void
  voteFaq: (faqId: number, vote: 'up' | 'down') => void
  clearFaqSearch: () => void
  clearSelectedFaq: () => void

  // Async actions (with React Query integration)
  loadFaqTopics: () => Promise<FaqTopic[]>
  loadFaq: (slug: string) => Promise<Faq>
  searchFaqs: (query: Record<string, any>) => Promise<FaqSearchResult>
  loadRelatedFaqs: (topicId: number) => Promise<Faq[]>
}

export const useFaqStore = create<FaqStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        faqsLoading: false,
        relatedFaqsLoading: false,
        allFaqTopics: [],
        selectedFaq: null,
        relatedFaqs: [],
        faqSearchResults: [],
        votedFaqs: {},
        previouslySelectedFaqTopic: null,
        selectedFaqTopic: null,

        // Computed selectors
        getFaqById: (id: number) => {
          const { selectedFaq, relatedFaqs, faqSearchResults } = get()

          // Check selected FAQ first
          if (selectedFaq?.id === id) {
            return selectedFaq
          }

          // Check related FAQs
          const relatedFaq = relatedFaqs.find(faq => faq.id === id)
          if (relatedFaq) {
            return relatedFaq
          }

          // Check search results
          return faqSearchResults.find(faq => faq.id === id)
        },

        getFaqBySlug: (slug: string) => {
          const { selectedFaq, relatedFaqs, faqSearchResults } = get()

          // Check selected FAQ first
          if (selectedFaq?.slug === slug) {
            return selectedFaq
          }

          // Check related FAQs
          const relatedFaq = relatedFaqs.find(faq => faq.slug === slug)
          if (relatedFaq) {
            return relatedFaq
          }

          // Check search results
          return faqSearchResults.find(faq => faq.slug === slug)
        },

        getFaqTopicById: (id: number) => {
          const { allFaqTopics } = get()
          return allFaqTopics.find(topic => topic.id === id)
        },

        getFaqTopicBySlug: (slug: string) => {
          const { allFaqTopics } = get()
          return allFaqTopics.find(topic => topic.slug === slug)
        },

        hasSearchResults: () => {
          const { faqSearchResults } = get()
          return faqSearchResults.length > 0
        },

        getVoteForFaq: (faqId: number) => {
          const { votedFaqs } = get()
          return votedFaqs[faqId] || null
        },

        // Actions
        setFaqsLoading: (loading: boolean) =>
          set(state => {
            state.faqsLoading = loading
          }),

        setFaqTopics: (topics: FaqTopic[]) =>
          set(state => {
            state.allFaqTopics = topics
            state.faqsLoading = false
          }),

        setSelectedFaq: (faq: Faq | null) =>
          set(state => {
            state.selectedFaq = faq
          }),

        setRelatedFaqs: (faqs: Faq[]) =>
          set(state => {
            state.relatedFaqs = faqs
          }),

        setFaqSearchResults: (results: Faq[]) =>
          set(state => {
            state.faqSearchResults = results
          }),

        setSelectedFaqTopic: (topic: FaqTopic | null) =>
          set(state => {
            if (state.selectedFaqTopic?.id === topic?.id) {
              return
            }
            state.previouslySelectedFaqTopic = state.selectedFaqTopic
            state.selectedFaqTopic = topic
          }),

        setPreviouslySelectedFaqTopic: (topic: FaqTopic | null) =>
          set(state => {
            state.previouslySelectedFaqTopic = topic
          }),

        voteFaq: (faqId: number, vote: 'up' | 'down') =>
          set(state => {
            state.votedFaqs[faqId] = vote
          }),

        clearFaqSearch: () =>
          set(state => {
            state.faqSearchResults = []
          }),

        clearSelectedFaq: () =>
          set(state => {
            state.selectedFaq = null
            state.relatedFaqs = []
          }),

        // Async actions (integrate with our API services)
        loadFaqTopics: async () => {
          const store = get()
          // Guard against concurrent calls
          if (store.faqsLoading) {
            return store.allFaqTopics
          }

          store.setFaqsLoading(true)

          try {
            const topics = await faqService.getFaqTopics()
            store.setFaqTopics(topics)
            return topics
          } catch (error) {
            store.setFaqsLoading(false)
            store.setFaqTopics([])
            console.error('Failed to load FAQ topics:', error)
            throw error
          }
        },

        loadFaq: async (slug: string) => {
          if (get().selectedFaq?.slug === slug) {
            return get().selectedFaq!
          }
          try {
            const faq = await faqService.getFaq(slug)
            get().setSelectedFaq(faq)
            return faq
          } catch (error) {
            console.error('Failed to load FAQ:', error)
            throw error
          }
        },

        searchFaqs: async (query: Record<string, any>) => {
          try {
            const searchResult = await faqService.searchFaqs(query)
            get().setFaqSearchResults(searchResult.faqs)
            return searchResult
          } catch (error) {
            get().setFaqSearchResults([])
            console.error('Failed to search FAQs:', error)
            throw error
          }
        },

        loadRelatedFaqs: async (topicId: number) => {
          if (get().relatedFaqsLoading) {
            return get().relatedFaqs
          }
          set(state => {
            state.relatedFaqsLoading = true
          })
          try {
            const faqs = await faqService.getFaqsByTopic(topicId)
            get().setRelatedFaqs(faqs)
            return faqs
          } catch (error) {
            get().setRelatedFaqs([])
            console.error('Failed to load related FAQs:', error)
            throw error
          } finally {
            set(state => {
              state.relatedFaqsLoading = false
            })
          }
        },
      })),
      {
        name: 'ivrpath-faqs',
        storage: createJSONStorage(() => localStorage),
        // Persist FAQ data and user interactions
        partialize: state => ({
          allFaqTopics: state.allFaqTopics,
          votedFaqs: state.votedFaqs,
          selectedFaqTopic: state.selectedFaqTopic,
          previouslySelectedFaqTopic: state.previouslySelectedFaqTopic,
          // Don't persist search results or selected FAQ as they're temporary
        }),
      }
    ),
    { name: 'FaqStore', enabled: import.meta.env.DEV }
  )
)

// Selector hooks for common use cases
export const useFaqTopics = () => useFaqStore(state => state.allFaqTopics)
export const useSelectedFaq = () => useFaqStore(state => state.selectedFaq)
export const useSelectedFaqTopic = () => useFaqStore(state => state.selectedFaqTopic)
export const useRelatedFaqs = () => useFaqStore(state => state.relatedFaqs)
export const useFaqSearchResults = () => useFaqStore(state => state.faqSearchResults)
export const useFaqsLoading = () => useFaqStore(state => state.faqsLoading)
export const useHasSearchResults = () => useFaqStore(state => state.hasSearchResults())
export const useVotedFaqs = () => useFaqStore(state => state.votedFaqs)
